<template>
  <v-card elevation="0">
    <v-card-text class="px-0 pt-0">
      <!-- 入店時間 start -->
      <table-start-at
        :table-id="tableId"
        :today="today"
        :start-at="startAt"
        :expire-at="expireAt"
        class="px-5 my-5"
        @updated:table="$emit('updated:table', $event)"
        @updated:vending-histories="$emit('updated:vending-histories', $event)"
      />
      <!-- 入店時間 end -->

      <!-- セルフオーダー start -->
      <self-order-qr-code
        v-if="isSelfOrderEnabled"
        :table-id="tableId"
        :table-number="tableNumber"
        :table-prefix="tablePrefix"
        :table-suffix="tableSuffix"
        :custom-name="customName"
        :self-order-status="selfOrderStatus"
        :hall-name="hallName"
        :start-at="startAt"
        class="px-4 mb-4"
        @updated:self-order-status="$emit('updated:table', { attributes: { selfOrderStatus: $event } })"
      />
      <!-- セルフオーダー end -->

      <v-card elevation="0">
        <v-card-text class="py-0 px-4 d-flex align-center justify-space-between">
          <!-- 客数 start -->
          <customer-count
            :table-id="tableId"
            :customer-count="customerCount"
            @open-order-dialog="isOrdering = true"
            @updated-customer-count="updateCustomerCount"
          />
          <!-- 客数 end -->

          <!-- 現在のお会計 start -->
          <div class="d-flex flex-column align-center">
            <div class="d-flex pr-2">
              <sup>
                <v-icon>
                  {{ icons.mdiCurrencyJpy }}
                </v-icon>
              </sup>
              <span
                class="text-2xl font-weight-semibold"
              >
                {{ total.toLocaleString() }}
              </span>
            </div>

            <small class="text--secondary text-xs mt-2">
              (小計：¥{{ subtotal.toLocaleString() }})
            </small>
          </div>
          <!-- 現在のお会計 end -->

          <!-- 合流注文ダイアログ start -->
          <order-dialog
            :users="users"
            :is-ordering="isOrdering"
            :is-submitting="isSubmitting"
            :is-valid-to-submit="isValidToSubmit"
            :is-confirmed="isConfirmed"
            :current-total="total"
            :order-total="estimatedTotal"
            :vending-histories-temp="courseHistoriesTemp"
            :vendibles="availableCourses"
            :estimated-subtotal="estimatedSubtotal"
            :estimated-tax="estimatedTax"
            :vending-histories="vendingHistories"
            :persistence-report="persistenceReport"
            @update-vending-histories="updateVendingHistories"
            @create-vending-histories="createVendingHistories"
            @cancel="isOrdering = false"
          />
          <!-- 注文ダイアログ end -->
        </v-card-text>

        <customer-table-tagging
          :table-id="tableId"
          :customer-table-taggings="customerTableTaggings"
          class="px-4"
          @updated:table="$emit('updated:table', $event)"
        />

        <customer-select
          ref="customerSelect"
          v-model="selectedCustomerIds"
          :initial-customer-ids="customerIds"
          class="px-4 mt-2"
          @change="updateCustomers"
        />

        <!-- セット中の顧客の詳細プレビュー(注意事項は常時表示・タップでポップオーバー) -->
        <div class="px-4 pb-2">
          <customer-detail-card
            v-for="customerId in selectedCustomerIds"
            :key="`customer-detail-${customerId}`"
            :customer-id="customerId"
          />
        </div>
      </v-card>

      <v-divider class="mt-2 mb-1 mx-4" />

      <!-- リファラル start -->
      <referring-histories
        :table-id="tableId"
        @updated:referring-histories="$emit('updated:referring-histories', $event)"
        @updated:table="$emit('updated:table', $event)"
      />
      <!-- リファラル end -->

      <v-divider class="mt-2 mb-1 mx-4" />

      <!-- 自動延長 start -->
      <div class="px-4 my-2">
        <auto-extension-panel
          :table-id="tableId"
          :expire-at="expireAt"
          :auto-extension-enabled="autoExtensionEnabled"
          :auto-extension-course-name="autoExtensionCourseName"
          @updated:table="$emit('updated:table', $event)"
        />
      </div>
      <v-divider class="mt-2 mb-1 mx-4" />
      <!-- 自動延長 end -->

      <div v-if="courseOverview && courseOverview.attributes">
        <!-- コース start -->
        <course-histories
          :table-id="tableId"
          :expire-at="expireAt"
          :course-overview="courseOverview"
          @updated:vending-histories="$emit('updated:vending-histories', $event)"
          @updated:table="$emit('updated:table', $event)"
        />
        <!-- コース end-->

        <v-divider class="mt-2 mb-1 mx-4" />
      </div>

      <div v-if="currentNominations && currentNominations.length > 0">
        <!-- ノミネーション start -->
        <nomination-histories
          :table-id="tableId"
          :current-nominations="currentNominations"
          @updated:vending-histories="$emit('updated:vending-histories', $event)"
          @updated:table="$emit('updated:table', $event)"
        />
        <!-- ノミネーション end -->

        <v-divider class="mt-2 mb-1 mx-4" />
      </div>

      <!-- 延長 start -->
      <extension-candidate-courses
        :key="ExtensionCandidateCoursesCompKey"
        :table-id="tableId"
        :table-number="tableNumber"
        :table-prefix="tablePrefix"
        :table-suffix="tableSuffix"
        :custom-name="customName"
        :start-at="startAt"
        :expire-at="expireAt"
        :customer-count="customerCount"
        :total="total"
        :paid-in-advance-amount="paidInAdvanceAmount"
        :total-deposit-amount="totalDepositAmount"
        :customer-ids="customerIds"
        @updated:vending-histories="$emit('updated:vending-histories', $event)"
        @updated:table="$emit('updated:table', $event)"
        @reload="ExtensionCandidateCoursesCompKey = `${Math.random()}`"
        @order="$emit('order')"
      />
      <!-- 延長 end -->
    </v-card-text>
  </v-card>
</template>

<script>
// TODO(移行期の旧UI・撤去予定): useTableOverview3Column 撤去時にこのファイルごと削除する。
//   移行期間中はバグ修正のみ（詳細は useFeatureFlags.js の同フラグのTODO参照）。
import {
  ref, watch, computed, getCurrentInstance, inject,
} from '@vue/composition-api'
import {
  sumBy, cloneDeep, isEqual, includes, each, update,
} from 'lodash'
import { mdiCurrencyJpy } from '@mdi/js'
import useVendingHistory from '@/views/composable/useVendingHistory'
import useCurrentData from '@/views/composable/useCurrentData'
import CustomerTableTagging from '@/views/components/table/CustomerTableTagging.vue'
import CustomerSelect from '@/views/components/table/CustomerSelect.vue'
import CustomerDetailCard from '@/views/components/table/CustomerDetailCard.vue'
import TableStartAt from './TableStartAt.vue'
import CustomerCount from './CustomerCount.vue'
import ReferringHistories from './ReferringHistories.vue'
import CourseHistories from './CourseHistories.vue'
import NominationHistories from './NominationHistories.vue'
import ExtensionCandidateCourses from './ExtensionCandidateCourses.vue'
import OrderDialog from '../OrderDialog.vue'
import SelfOrderQrCode from '@/views/components/table/SelfOrderQrCode.vue'
import AutoExtensionPanel from './AutoExtensionPanel.vue'
import TableApi from '@/api/waiter/Table'
import VendingHistoryApi from '@/api/waiter/VendingHistory'
import useKitchenPrint from '@/views/composable/useKitchenPrint'

export default {
  components: {
    TableStartAt,
    CustomerCount,
    ReferringHistories,
    CourseHistories,
    NominationHistories,
    ExtensionCandidateCourses,
    CustomerTableTagging,
    CustomerSelect,
    CustomerDetailCard,
    OrderDialog,
    SelfOrderQrCode,
    AutoExtensionPanel,
  },
  props: {
    tableId: {
      type: [Number, String],
      validator: value => !Number.isNaN(Number(value)),
      required: true,
    },
    tableNumber: {
      type: [Number, String],
      validator: value => !Number.isNaN(Number(value)),
      required: true,
    },
    tablePrefix: {
      type: String,
      default: '',
    },
    tableSuffix: {
      type: String,
      default: '卓',
    },
    customName: {
      type: String,
      default: '',
    },
    today: {
      type: String,
      required: true,
    },
    startAt: {
      type: String,
      required: true,
    },
    expireAt: {
      type: String,
      required: false,
      default: null,
    },
    autoExtensionEnabled: {
      type: Boolean,
      default: false,
    },
    autoExtensionCourseName: {
      type: String,
      default: null,
    },
    customerCount: {
      type: [Number, String],
      validator: value => !Number.isNaN(Number(value)),
      required: true,
    },
    subtotal: {
      type: [Number, String],
      validator: value => !Number.isNaN(Number(value)),
      required: true,
    },
    total: {
      type: [Number, String],
      validator: value => !Number.isNaN(Number(value)),
      required: true,
    },
    paidInAdvanceAmount: {
      type: [Number, String],
      required: false,
      default: 0,
    },
    currentNominations: {
      // NOTE: 中身は今生きてるNominationのvending_history
      type: Array,
      default: () => [],
    },
    courseOverview: {
      // NOTE: 中身はその卓で一番終わるまでの時間が長いコースのvendingHistory
      type: Object,
      default: () => {},
    },
    customerTableTaggings: {
      type: Array,
      default: () => [],
    },
    customerIds: {
      type: Array,
      default: () => [],
    },
    courses: {
      type: Array,
      default: () => [],
    },
    selfOrderStatus: {
      type: String,
      default: null,
    },
    hallName: {
      type: String,
      default: '',
    },
    deposits: {
      type: Array,
      default: () => [],
    },
  },
  setup(props, { emit }) {
    const vm = getCurrentInstance().proxy
    const users = inject('users', [])
    const { currentClub, currentUser } = useCurrentData()
    const isSelfOrderEnabled = computed(() => currentClub.value?.selfOrderEnabled)
    const { printKitchenSlips } = useKitchenPrint()
    const ExtensionCandidateCoursesCompKey = ref(`${Math.random()}`)
    const isOrdering = ref(false)
    const isSubmitting = ref(false)
    const isValidToSubmit = ref(true)
    const isConfirmed = ref({
      updateVendingHistories: false,
    })
    const vendingHistoriesTemp = ref([])
    const availableCourses = ref(props.courses.length > 0 ? props.courses : [])
    // props.totalのcopyを作成, 名前は注文後の合計
    const orderTotal = ref(props.total)
    const totalDepositAmount = computed(() => {
      return props.deposits.reduce((sum, d) => sum + (d.amount || 0), 0)
    })
    const persistenceReport = ref({})
    const estimatedTax = ref(0)
    const selectedCustomerIds = ref([...props.customerIds])

    // 卓データは非同期で届くため、props.customerIdsの反映を待って同期する
    // (CustomerSelectはinitialCustomerIdsのwatchで追従するが、こちらのv-forは追従しないため)
    watch(() => props.customerIds, newVal => {
      if (isEqual(newVal, selectedCustomerIds.value)) return

      selectedCustomerIds.value = [...(newVal || [])]
    })
    const customerSelect = ref(null)

    const {
      vendingHistories,
      addVendingHistoryTemp,
      courseHistoriesTemp,
      vendingHistoriesToSubmit,
      setLastPersistenceReport,
      setVendingContributionsAttributes,
      resetVendingHistoriesToSubmit,
    } = useVendingHistory()

    const updateVendingHistories = () => {
      emit('updateVendingHistories')
    }

    const updateCustomerCount = async customerCount => {
      const res = await TableApi.updateTable({
        tableId: props.tableId,
        params: { customerCount },
      })

      if (res?.data?.data) {
        emit('updated:table', res.data.data)

        vm.$toast.success('客数を更新しました')
      }
    }

    const updateCustomers = async customerIds => {
      const res = await TableApi.updateTable({
        tableId: props.tableId,
        params: { customerIds },
      })

      if (res?.data?.data) {
        emit('updated:table', res.data.data)
        selectedCustomerIds.value = customerIds

        vm.$toast.success('顧客を更新しました')
      }
    }

    const createVendingHistories = async () => {
      isSubmitting.value = true

      if (isValidToSubmit.value) {
        const res = await VendingHistoryApi.createVendingHistories({
          tableId: props.tableId,
          vendingHistories: vendingHistoriesToSubmit.value,
        })

        if (res?.status === 200) {
          emit('updated:vending-histories', res.data.vendingHistories.data)
          emit('updated:table', res.data.table.data)
          const customerCount = vendingHistoriesToSubmit.value.length + props.customerCount
          updateCustomerCount(customerCount)

          // キッチンプリンター印刷
          const tableCode = props.customName || `${props.tablePrefix}${props.tableNumber}${props.tableSuffix}`
          printKitchenSlips({
            vendingHistories: res.data.vendingHistories.data,
            staffName: currentUser.value?.name || '',
            hallName: props.hallName,
            tableCode,
            customerCount: props.customerCount,
          })

          resetVendingHistoriesToSubmit()

          vm.$toast.success('注文しました')
        }
      }

      isSubmitting.value = false
      isOrdering.value = false
    }

    const getLastPersistenceReport = () => {
      const courseVendingHistories = vendingHistories.value.filter(obj => obj.attributes.vendibleType === 'Course')

      if (courseVendingHistories.length === 0) {
        // 既存 Course が無い場合は、選択中コースの effectiveDurationSeconds をベースに
        // 卓の入店時間（props.startAt）からのデフォルト時間レンジを返す。
        // コースに duration が無い、または入店時間が無い場合は妥当なデフォルトが
        // 算出できないため空を返し、時間入力フォームは表示しない。
        const targetCourse = props.courses?.[0]
        const durationSeconds = targetCourse?.attributes?.effectiveDurationSeconds
        if (!durationSeconds || !props.startAt) return {}

        const startAt = new Date(props.startAt)
        const releaseAt = new Date(startAt.getTime() + durationSeconds * 1000)

        return {
          data: {
            attributes: {
              startAt: startAt.toISOString(),
              releaseAt: releaseAt.toISOString(),
            },
          },
        }
      }

      const recentVendingHistory = courseVendingHistories.sort((a, b) => {
        return new Date(b.attributes.persistenceReport.data.attributes.startAt) - new Date(a.attributes.persistenceReport.data.attributes.startAt)
      })[0]

      return {
        data: {
          attributes: {
            startAt: recentVendingHistory.attributes.persistenceReport.data.attributes.startAt,
            releaseAt: recentVendingHistory.attributes.persistenceReport.data.attributes.releaseAt,
          },
        },
      }
    }

    const calcTotal = async (price = null) => {
      const res = await TableApi.calcTotal({
        tableId: props.tableId,
        price,
      })

      return res?.data
    }

    const calcTax = async price => {
      const res = await TableApi.calcTax({
        tableId: props.tableId,
        price,
      })

      return res?.data
    }

    const addVendingHistoriesSubtotal = computed(() => {
      return sumBy(courseHistoriesTemp.value, obj => {
        return +obj.value.sellingPrice * obj.size
      })
    })

    const estimatedSubtotal = computed(() => {
      // NOTE: vendingHistoriesはcomposableでinjectされている
      const vendingHistoriesSubtotal = sumBy(vendingHistories.value, obj => {
        return +obj.attributes.sellingPrice
      })

      // 追加注文の合計
      return vendingHistoriesSubtotal + addVendingHistoriesSubtotal.value
    })

    const estimatedTotal = computed(() => {
      return Number(orderTotal.value)
    })

    const updateEstimatedTax = async () => {
      if (orderTotal.value <= 0) {
        estimatedTax.value = 0

        return
      }

      const vendingHistoriesTax = sumBy(vendingHistories.value, obj => {
        // NOTE: tax込み/カットは除外
        if (includes(['cut', 'included'], obj.attributes.taxCharge)) return 0

        return +obj.attributes.taxPrice
      })

      // courseHistoriesTempのestimatedTaxPriceの合計を計算
      const courseHistoriesTax = sumBy(courseHistoriesTemp.value, obj => {
        return (obj.estimatedTaxPrice * obj.size) || 0
      })

      estimatedTax.value = vendingHistoriesTax + courseHistoriesTax
    }

    // コースを追加するときに注文後の合計を計算, 手動で単価変更した場合も更新される
    watch(() => cloneDeep(courseHistoriesTemp.value), (_newVal, _prevVal) => {
      // 各コースのestimatedTaxPriceを更新
      each(courseHistoriesTemp.value, obj => {
        if (obj.value.taxCharge === 'normal') {
          calcTax(obj.value.sellingPrice).then(taxPrice => {
            update(obj, 'estimatedTaxPrice', () => taxPrice)
            updateEstimatedTax() // estimatedTaxPriceが更新されたら再計算
          })
        } else {
          update(obj, 'estimatedTaxPrice', () => 0)
          updateEstimatedTax() // estimatedTaxPriceが更新されたら再計算
        }
        setVendingContributionsAttributes({
          targetVendingHistoriesTemp: courseHistoriesTemp.value,
        })
      })
    })

    watch(() => cloneDeep([estimatedSubtotal.value, estimatedTax.value]), (newVal, prevVal) => {
      if (isEqual(newVal, prevVal)) return

      const rawEstimatedTotal = estimatedSubtotal.value + estimatedTax.value
      calcTotal(rawEstimatedTotal).then(res => {
        orderTotal.value = res
      })
    }, { immediate: true })

    // 初回にコースを追加する。このコースはこの卓でデオフォルトにセットされているもの
    watch(props.courses, (newVal, _) => {
      if (props.courses.length > 0) {
        addVendingHistoryTemp({
          vendibleId: props.courses[0].id,
          vendibleType: 'Course',
          sellingPrice: newVal[0].attributes.sellingPrice,
          vendibleDefaultTaxCharge: newVal[0].attributes.defaultTaxCharge,
        })
        updateEstimatedTax()

        const latestPersistenceReport = getLastPersistenceReport()
        setLastPersistenceReport(courseHistoriesTemp.value[0], latestPersistenceReport)
      }
    }, { immediate: true })

    // 卓の入店時間（startAt）が更新されたら、既存の courseHistoriesTemp[0] の
    // persistenceReport を再計算して反映する（drawer を閉じずに反映するため）
    watch(() => props.startAt, () => {
      if (courseHistoriesTemp.value.length === 0) return

      const latestPersistenceReport = getLastPersistenceReport()
      setLastPersistenceReport(courseHistoriesTemp.value[0], latestPersistenceReport)
    })

    return {
      ExtensionCandidateCoursesCompKey,
      isOrdering,
      isSubmitting,
      isValidToSubmit,
      isConfirmed,
      users,
      isSelfOrderEnabled,
      vendingHistoriesTemp,
      vendingHistories,
      courseHistoriesTemp,
      availableCourses,
      estimatedSubtotal,
      estimatedTotal,
      orderTotal,
      totalDepositAmount,
      estimatedTax,
      persistenceReport,
      selectedCustomerIds,
      customerSelect,
      updateVendingHistories,
      createVendingHistories,
      updateCustomerCount,
      updateCustomers,
      icons: {
        mdiCurrencyJpy,
      },
    }
  },
}
</script>
